import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, TrendingUp, Target, Gamepad2, Trophy, ExternalLink } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import { TierBadge } from "@/components/TierBadge";

export default function PlayerDetail() {
  const { id } = useParams();
  const [player, setPlayer] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, h] = await Promise.all([
          api.get(`/players/${id}`),
          api.get(`/players/${id}/history`),
        ]);
        setPlayer(p.data);
        setHistory(h.data);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-40 bg-[#1A1A1D] rounded" />
          <div className="h-48 bg-[#1A1A1D] rounded-xl" />
        </div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
        <p className="text-[#E84057] mb-4" data-testid="player-error">{error || "Brak gracza"}</p>
        <Link to="/" className="text-[#C8AA6E] hover:underline">
          ← Powrót do tabeli
        </Link>
      </div>
    );
  }

  const chartData = history.map((h, idx) => ({
    idx: idx + 1,
    lp: h.league_points,
    placement: h.avg_placement || 0,
    when: new Date(h.captured_at).toLocaleString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }),
  }));

  const lolchessUrl = `https://lolchess.gg/profile/${player.region}/${player.game_name}-${player.tag_line}/${player.season || "set17"}`;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link
        to="/"
        data-testid="back-to-leaderboard"
        className="inline-flex items-center gap-2 text-sm text-[#A09B8C] hover:text-[#C8AA6E] mb-6"
      >
        <ArrowLeft className="h-4 w-4" /> Powrót do tabeli
      </Link>

      <div className="rounded-2xl border border-[#2A2A30] bg-[#121214] p-6 md:p-8 mb-8">
        <div className="flex flex-col md:flex-row items-start gap-6">
          {player.profile_icon_url && (
            <img
              src={player.profile_icon_url}
              alt={player.game_name}
              className="h-20 w-20 md:h-24 md:w-24 rounded-xl border border-[#2A2A30] object-cover"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.25em] text-[#6E6A5E] font-bold mb-2">
              {player.region} · Set 17
            </div>
            <h1 className="font-[Outfit] text-3xl sm:text-4xl font-black tracking-tight text-[#F0E6D2]">
              {player.nickname || player.game_name}
            </h1>
            <div className="text-sm text-[#A09B8C] mb-4">
              {player.game_name}#{player.tag_line} · poziom {player.summoner_level}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <TierBadge tier={player.tier} rank={player.rank} lp={player.league_points} size="lg" />
              {player.tier_global_rank && (
                <span className="text-xs text-[#A09B8C]">
                  Miejsce w rankingu: <span className="text-[#F0E6D2] font-bold">#{player.tier_global_rank.toLocaleString("pl-PL")}</span>
                  {player.tier_global_total && ` / ${player.tier_global_total.toLocaleString("pl-PL")}`}
                </span>
              )}
              <a
                href={lolchessUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="lolchess-link"
                className="inline-flex items-center gap-1.5 text-xs text-[#0AC8B9] hover:text-[#3FE3D6] font-semibold"
              >
                <ExternalLink className="h-3.5 w-3.5" /> lolchess.gg
              </a>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
          <BigStat icon={Gamepad2} label="Rozegrane gry" value={player.plays} />
          <BigStat
            icon={Target}
            label="Średnie miejsce"
            value={player.avg_placement ? player.avg_placement.toFixed(2) : "—"}
            accent
          />
          <BigStat icon={Trophy} label="Top 4" value={`${Math.round(player.top4_rate || 0)}%`} />
          <BigStat icon={TrendingUp} label="Winrate" value={`${Math.round(player.win_rate || 0)}%`} />
        </div>
      </div>

      <div className="rounded-2xl border border-[#2A2A30] bg-[#121214] p-6 md:p-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#6E6A5E] font-bold">
              Historia
            </div>
            <h2 className="font-[Outfit] text-2xl font-bold text-[#F0E6D2]">
              Progres LP
            </h2>
          </div>
          <div className="text-xs text-[#A09B8C]">{history.length} pomiarów</div>
        </div>

        {chartData.length < 2 ? (
          <div className="h-64 flex items-center justify-center text-sm text-[#6E6A5E]">
            Za mało danych, by narysować wykres. Odśwież statystyki kilkukrotnie z panelu admina.
          </div>
        ) : (
          <div className="h-64" data-testid="lp-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ left: 0, right: 12, top: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="lpFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0AC8B9" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#0AC8B9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#2A2A30" strokeDasharray="3 3" />
                <XAxis dataKey="when" stroke="#6E6A5E" tick={{ fontSize: 11 }} />
                <YAxis stroke="#6E6A5E" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#0A0A0B",
                    border: "1px solid #2A2A30",
                    borderRadius: 8,
                    color: "#F0E6D2",
                    fontSize: 12,
                  }}
                  labelStyle={{ color: "#6E6A5E" }}
                />
                <Area
                  type="monotone"
                  dataKey="lp"
                  stroke="#0AC8B9"
                  strokeWidth={2}
                  fill="url(#lpFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function BigStat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-lg border border-[#2A2A30] bg-[#0A0A0B]/40 px-4 py-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#6E6A5E] font-bold">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div
        className={`mt-1 font-[Outfit] text-2xl md:text-3xl font-black tabular-nums tracking-tight ${
          accent ? "text-[#0AC8B9]" : "text-[#F0E6D2]"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
